import { faDownload } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Modal, Spin } from "antd";
import React from "react";
import PdfPagesPreview from "./PdfPagesPreview";

interface Props {
    title: React.ReactNode;
    // The left-hand panel: what to include and any other settings.
    options: React.ReactNode;
    // Shown with a spinner while there's no PDF to preview yet, e.g. "Drawing the map...".
    preparingLabel: string | null;
    blob: Blob | null;
    version: number;
    rendering: boolean;
    onDownload: () => void;
    onClose: () => void;
    // Shown in place of the preview while set, like the map framing editor. Downloading waits until it's closed.
    editor?: React.ReactNode;
    // Off-screen helpers the export needs while open, like a map being captured.
    children?: React.ReactNode;
}

// The export dialog shared by PLOT's PDFs: settings on the left, the real generated PDF on the right, and a download
// that's exactly what's previewed.
const PdfExportDialogShell: React.FC<Props> = ({
    title,
    options,
    preparingLabel,
    blob,
    version,
    rendering,
    onDownload,
    onClose,
    editor,
    children,
}) => (
    <Modal
        title={title}
        open
        onCancel={onClose}
        width={1100}
        style={{ top: 24 }}
        footer={[
            <Button key="cancel" onClick={onClose}>
                Cancel
            </Button>,
            <Button
                key="download"
                type="primary"
                icon={<FontAwesomeIcon icon={faDownload} />}
                disabled={!blob || rendering || !!editor}
                onClick={onDownload}
            >
                Download PDF
            </Button>,
        ]}
    >
        <div className="flex flex-col md:flex-row gap-5">
            <aside className="md:w-64 shrink-0 flex flex-col gap-4">
                <p className="text-xs text-gray-500 m-0">
                    Choose what to include. The preview updates as you go, and the download matches it exactly.
                </p>
                {options}
            </aside>

            {/* The scrollbar's space is always reserved: otherwise a page that only just overflows narrows the preview,
                gets drawn smaller, stops overflowing, widens it again, and redraws forever. */}
            <section className="relative flex-1 min-w-0 max-h-[70vh] overflow-y-auto [scrollbar-gutter:stable] rounded-lg bg-gray-100 p-3">
                {editor ? (
                    editor
                ) : !blob ? (
                    <div className="flex h-80 flex-col items-center justify-center gap-3 text-sm text-gray-500">
                        <Spin />
                        {preparingLabel || "Rendering..."}
                    </div>
                ) : (
                    <PdfPagesPreview blob={blob} version={version} />
                )}
                {rendering && blob && !editor && (
                    <div className="absolute inset-0 flex items-start justify-center pt-10 bg-white/40">
                        <Spin />
                    </div>
                )}
            </section>
        </div>
        {children}
    </Modal>
);

export const PdfOptionGroup: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <fieldset className="flex flex-col gap-1.5">
        <legend className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">{title}</legend>
        {children}
    </fieldset>
);

export default PdfExportDialogShell;
